const TO_EU = ["EUR", "GBP"];
const TO_CA = ["CAD"];

export const cleanDigits = (s) => (s || "").replace(/[^\d]/g, "");

export function buildXborderFields(cbTo, cbFields, cbBankName, cbUsd) {
    const to = (cbTo || "").toUpperCase();

    if (to === "USD") {
        const u = cbUsd || {};
        const acctTypeRaw = String(u.account_type || "us").toLowerCase();
        const acctType = acctTypeRaw === "swift" ? "international_wire" : acctTypeRaw;

        const bankName = (cbBankName || u.bank_name || "").trim();
        const recipientName = (
            u.account_owner_name ||
            [u.first_name, u.last_name].filter(Boolean).join(" ").trim() ||
            (u.business_name || "").trim()
        ).trim();

        if (acctType === "iban") {
            return {
                bankName,
                recipientName,
                iban: (u.iban?.account_number || "").replace(/\s+/g, ""),
                swiftCode: (u.iban?.bic || "").trim(),
                country: (u.iban?.country || u.address?.country || "").trim(),
                routingNumber: cleanDigits(u.swift?.routing_number || ""),
            };
        }

        if (acctType === "international_wire" || acctType === "swift") {
            return {
                bankName,
                recipientName,
                accountNumber: (u.swift?.account_number || "").trim(),
                swiftCode: (u.swift?.bic || "").trim(),
                country: (u.swift?.country || u.address?.country || "").trim(),
            };
        }

        return {
            bankName,
            recipientName,
            routingNumber: cleanDigits(u.account?.routing_number || ""),
            accountNumber: (u.account?.account_number || "").trim(),
        };
    }

    if (TO_CA.includes(to)) {
        return {
            transitNumber: cleanDigits(cbFields.transitNumber),
            institutionNumber: cleanDigits(cbFields.institutionNumber),
            accountNumber: (cbFields.accountNumber || "").trim(),
            recipientName: (cbFields.accountName || "").trim(),
            bankName: cbBankName || "",
        };
    }

    if (TO_EU.includes(to)) {
        const ibanNumber = (cbFields.iban || cbFields.accountNumber || "").replace(/\s+/g, "");
        const payload = {
            iban: { account_number: ibanNumber },
            swiftCode: (cbFields.swiftBic || cbFields.swift || cbFields.bic || "").trim(),
            recipientName: (cbFields.accountName || "").trim(),
            bankName: cbBankName || "",
        };
        if (to === "GBP" && cbFields.sortCode) {
            payload.sortCode = cbFields.sortCode.replace(/[^\d-]/g, "");
        }
        return payload;
    }

    return {
        bankCode: (cbFields.bankCode || "").trim(),
        accountNumber: (cbFields.accountNumber || "").trim(),
        recipientName: (cbFields.accountName || "").trim(),
        bankName: cbBankName || "",
        ...(cbFields.branchCode ? { branchCode: (cbFields.branchCode || "").trim() } : {}),
    };
}

export function buildUsdPayloadFromCrossBorder(cb, pin, sesid) {
    const cbTo = (cb?.recipient?.toCurrency || "").toUpperCase();
    const cbFrom = (cb?.fromCurrency || "").toUpperCase();
    const u = cb?.recipient?.usd || {};

    const acctTypeRaw = String(u.account_type || "us").toLowerCase();
    const acctType = acctTypeRaw === "swift" ? "international_wire" : acctTypeRaw;
    const paymentRail =
        (u.paymentRail === "iban" ? "wire" : u.paymentRail) ||
        (acctType === "iban" ? "wire" : acctType === "international_wire" ? "swift" : "ach");

    const bank_name = (u.bank_name || cb?.recipient?.bankName || "").trim();
    const account_owner_type = (u.account_owner_type || "individual").trim();

    const first_name = (u.first_name || "").trim();
    const last_name = (u.last_name || "").trim();
    const business_name = (u.business_name || "").trim();

    const account_owner_name = (
        u.account_owner_name ||
        (account_owner_type === "corporate"
            ? business_name
            : [first_name, last_name].filter(Boolean).join(" ").trim())
    ).trim();

    const address = u.address || {};
    const addressBlock = {
        street_line_1: (address.street_line_1 || "").trim(),
        street_line_2: (address.street_line_2 || "").trim(),
        city: (address.city || "").trim(),
        state: (address.state || "").trim(),
        subdivision: (address.subdivision || "").trim(),
        postal_code: (address.postal_code || "").trim(),
        country: (address.country || "").trim(),
    };
    const bankAddress = u.bank_address || {};
    const bankAddressBlock = {
        street_line_1: (bankAddress.street_line_1 || "").trim(),
        street_line_2: (bankAddress.street_line_2 || "").trim(),
        city: (bankAddress.city || "").trim(),
        state: (bankAddress.state || "").trim(),
        postal_code: (bankAddress.postal_code || "").trim(),
        country: (bankAddress.country || "").trim(),
    };

    let methodBlock = {};
    if (acctType === "iban") {
        methodBlock = {
            iban: {
                account_number: (u.iban?.account_number || "").replace(/\s+/g, ""),
                bic: (u.iban?.bic || "").trim(),
                country: (u.iban?.country || addressBlock.country || "").trim(),
            },
        };
    } else if (acctType === "international_wire" || acctType === "swift") {
        methodBlock = {
            swift: {
                account_number: (u.swift?.account_number || "").trim(),
                bic: (u.swift?.bic || "").trim(),
                country: (u.swift?.country || addressBlock.country || "").trim(),
            },
        };
    } else {
        methodBlock = {
            account: {
                account_number: (u.account?.account_number || "").trim(),
                routing_number: cleanDigits(u.account?.routing_number || ""),
                checking_or_savings: (u.account?.checking_or_savings || "checking").trim(),
            },
        };
    }

    return {
        amount: String(cb?.amount || ""),
        sesid,
        pin,
        currency: "USD",
        currencyFrom: cbFrom || "USD",
        bank_name,
        account_owner_name,
        account_type: acctType,
        paymentRail,
        account_owner_type,
        first_name: account_owner_type === "individual" ? first_name : "",
        last_name: account_owner_type === "individual" ? last_name : "",
        business_name: account_owner_type === "corporate" ? business_name : "",
        address: addressBlock,
        relationship: String(u.relationship || "").trim(),
        email: String(u.email || "").trim(),
        ...(acctType === "us"
            ? {
                  bank_address: bankAddressBlock,
              }
            : {}),
        naration: String(cb?.memo || "").trim(),
        rateKey: String(cb?.rateKey || "").trim(),
        ...(cb?.recipient?.beneficiaryId ? { beneficiaryId: cb.recipient.beneficiaryId } : {}),
        ...(cb?.recipient?.transactionId ? { transactionId: cb.recipient.transactionId } : {}),
        ...methodBlock,
        currencyTo: cbTo || "USD",
        isConversion: false,
    };
}

export function validateXborder(cbTo, fields, usdAccountType = "us") {
    const to = (cbTo || "").toUpperCase();

    if (to === "USD") {
        const t = String(usdAccountType || "us").toLowerCase();
        const req =
            t === "iban"
                ? ["iban", "swiftCode", "country", "recipientName", "bankName"]
                : t === "international_wire" || t === "swift"
                  ? ["accountNumber", "swiftCode", "country", "recipientName", "bankName"]
                  : ["routingNumber", "accountNumber", "recipientName", "bankName"];

        const missing = req.filter((k) => !fields[k]);
        return { ok: missing.length === 0, missing };
    }

    const reqByTo = {
        USD: ["routingNumber", "accountNumber", "recipientName", "bankName"],
        CAD: ["transitNumber", "institutionNumber", "accountNumber", "recipientName"],
        EUR: ["iban", "swiftCode", "recipientName", "bankName"],
        GBP: ["iban", "swiftCode", "recipientName", "bankName"],
        NGN: ["bankCode", "accountNumber", "recipientName", "bankName"],
        KES: ["bankCode", "accountNumber", "recipientName", "bankName"],
        GHS: ["accountNumber", "recipientName", "bankName"],
    };

    const req = reqByTo[to] || [];
    const missing = req.filter((k) => !fields[k]);
    return { ok: missing.length === 0, missing };
}

export function deriveUsdReviewDetails(usdPayloadFromStore, usd) {
    const accountTypeRaw = String(usdPayloadFromStore?.account_type || usd?.accountType || "us").toLowerCase();
    const accountType = accountTypeRaw === "swift" ? "international_wire" : accountTypeRaw;

    const bankName = usdPayloadFromStore?.bank_name || usd?.bankName || "";
    const ownerName = usdPayloadFromStore?.account_owner_name || usd?.accountOwnerName || "";

    const address = usdPayloadFromStore?.address || {};
    const fullAddress = [
        address?.street_line_1,
        address?.city,
        address?.state,
        address?.postal_code,
        address?.country,
    ]
        .filter(Boolean)
        .join(", ");

    if (accountType === "iban") {
        return {
            accountType,
            bankName,
            ownerName,
            iban: usdPayloadFromStore?.iban?.account_number || "",
            bic: usdPayloadFromStore?.iban?.bic || "",
            country: usdPayloadFromStore?.iban?.country || address?.country || "",
            fullAddress,
        };
    }

    if (accountType === "international_wire") {
        return {
            accountType,
            bankName,
            ownerName,
            accountNumber: usdPayloadFromStore?.swift?.account_number || "",
            routingNumber: usdPayloadFromStore?.swift?.routing_number || "",
            bic: usdPayloadFromStore?.swift?.bic || "",
            country: usdPayloadFromStore?.swift?.country || address?.country || "",
            fullAddress,
        };
    }

    return {
        accountType: "us",
        bankName,
        ownerName,
        routingNumber: usdPayloadFromStore?.account?.routing_number || "",
        accountNumber: usdPayloadFromStore?.account?.account_number || "",
        checkingOrSavings: usdPayloadFromStore?.account?.checking_or_savings || "",
        fullAddress,
    };
}

export function deriveRecipientDetails({
    isBankRecipient,
    isCryptoFlow,
    isCryptoToFiatFlow,
    isCrossBorder,
    bankRecipient,
    cryptoTransfer,
    cfRecipient,
    cfNetwork,
    cfPayoutCurrency,
    cbTo,
    cbFields,
    cbBankName,
    cbUsdName,
    cbUsdAccountNumber,
    cbUsdBankName,
    cbUsdType,
    cbUsdRouting,
    cbUsdBic,
    cbUsdCountry,
    transferData,
}) {
    if (isBankRecipient) return bankRecipient;

    if (isCryptoFlow) {
        return {
            name: cryptoTransfer?.address ? "External wallet" : "",
            accountNumber: cryptoTransfer?.address || "",
            network: cryptoTransfer?.network || "",
        };
    }

    if (isCryptoToFiatFlow) {
        return {
            name: cfRecipient?.accountName || "",
            accountNumber: cfRecipient?.accountIdentifier || "",
            bankCode: cfRecipient?.institution || "",
            payoutCurrency: cfPayoutCurrency || "",
            bankName: cfRecipient?.bankName || "",
            network: cfNetwork || "",
        };
    }

    if (isCrossBorder) {
        if (cbTo === "USD") {
            return {
                name: cbUsdName || "",
                accountNumber: cbUsdAccountNumber || "",
                bankName: cbUsdBankName || "",
                extra: {
                    routingNumber: cbUsdType === "us" ? cbUsdRouting : "",
                    iban: cbUsdType === "iban" ? cbUsdAccountNumber : "",
                    swiftBic: cbUsdBic,
                    country: cbUsdCountry,
                    accountType: cbUsdType,
                    checkingOrSavings: "",
                },
                payoutCurrency: cbTo || "",
            };
        }

        return {
            name: cbFields?.accountName || "",
            accountNumber: cbFields?.accountNumber || cbFields?.iban || "",
            bankName: cbBankName || "",
            extra: {
                iban: cbFields?.iban || "",
                swiftBic: cbFields?.swiftBic || cbFields?.swift || cbFields?.bic || "",
                routingNumber: cbFields?.routingNumber || "",
                bankCode:
                    cbFields?.bankCode || cbFields?.routingNumber || cbFields?.sortCode || "",
                sortCode: cbFields?.sortCode || "",
                transitNumber: cbFields?.transitNumber || "",
                institutionNumber: cbFields?.institutionNumber || "",
            },
            payoutCurrency: cbTo || "",
        };
    }

    return transferData.recipient;
}

export function buildFeeText({
    feeLoading,
    isCryptoFlow,
    isCryptoToFiatFlow,
    isCrossBorder,
    isTradevuTX,
    backendFee,
    cryptoToken,
    cfCoin,
    cbFrom,
    currency,
    currencyPairCheck,
}) {
    if (feeLoading) return "Fetching fee…";
    if (isCryptoFlow) return `${(Number(backendFee) || 0).toLocaleString()} ${cryptoToken || "USDC"}`;
    if (isCryptoToFiatFlow) return `${(Number(backendFee) || 0).toLocaleString()} ${cfCoin}`;
    if (isCrossBorder) {
        return `${currencyPairCheck(cbFrom || currency)}${(Number(backendFee) || 0).toLocaleString()}`;
    }
    if (isTradevuTX) return `${currencyPairCheck(currency)}0`;
    return `${currencyPairCheck(currency)}${(Number(backendFee) || 0).toLocaleString()}`;
}

export function computeIsAlreadyBeneficiary({
    isBankRecipient,
    isCryptoToFiatFlow,
    isCrossBorder,
    beneficiaries,
    cfRecipient,
    cbFields,
    bankRecipient,
}) {
    if (!(isBankRecipient || isCryptoToFiatFlow || isCrossBorder)) return false;

    return beneficiaries.some((b) => {
        if (isCryptoToFiatFlow) {
            return (
                String(b.accountNumber) === String(cfRecipient?.accountIdentifier) &&
                String(b.bankCode) === String(cfRecipient?.institution)
            );
        }

        if (isCrossBorder) {
            return (
                String(b.accountNumber) === String(cbFields?.accountNumber) &&
                (String(b.bankCode) === String(cbFields?.bankCode) ||
                    String(b.bankCode) === String(cbFields?.routingNumber) ||
                    String(b.bankCode) === String(cbFields?.sortCode))
            );
        }

        return (
            String(b.accountNumber) === String(bankRecipient?.accountNumber) &&
            String(b.bankCode) === String(bankRecipient?.bankCode)
        );
    });
}

export function getYouSendText({
    isCryptoFlow,
    isCryptoToFiatFlow,
    isCrossBorder,
    displayAmount,
    cryptoToken,
    cfCoin,
    cbSymbol,
    currency,
    currencyPairCheck,
}) {
    if (isCryptoFlow) return `${displayAmount} ${cryptoToken || ""}`;
    if (isCryptoToFiatFlow) return `${displayAmount} ${cfCoin}`;
    if (isCrossBorder) return `${cbSymbol}${Number(displayAmount || 0).toLocaleString()}`;
    return `${currencyPairCheck(currency)}${displayAmount}`;
}

export function deriveCrossBorderUsdMeta(cb, cbBankName = "") {
    const cbUsd = cb?.recipient?.usd || {};
    const cbUsdTypeRaw = String(cbUsd?.account_type || "us").toLowerCase();
    const cbUsdType = cbUsdTypeRaw === "swift" ? "international_wire" : cbUsdTypeRaw;

    const cbUsdName = (
        cbUsd?.account_owner_name ||
        [cbUsd?.first_name, cbUsd?.last_name].filter(Boolean).join(" ").trim() ||
        (cbUsd?.business_name || "").trim()
    ).trim();

    const cbUsdBankName = (cbBankName || cbUsd?.bank_name || "").trim();
    const cbUsdAccountNumber =
        cbUsdType === "iban"
            ? (cbUsd?.iban?.account_number || "").trim()
            : cbUsdType === "international_wire" || cbUsdType === "swift"
              ? (cbUsd?.swift?.account_number || "").trim()
              : (cbUsd?.account?.account_number || "").trim();

    const cbUsdRouting = (cbUsd?.account?.routing_number || "").trim();
    const cbUsdBic = (cbUsd?.iban?.bic || cbUsd?.swift?.bic || "").trim();
    const cbUsdCountry =
        (cbUsdType === "iban"
            ? cbUsd?.iban?.country
            : cbUsdType === "international_wire" || cbUsdType === "swift"
              ? cbUsd?.swift?.country
              : cbUsd?.address?.country) || "";

    return {
        cbUsd,
        cbUsdType,
        cbUsdName,
        cbUsdBankName,
        cbUsdAccountNumber,
        cbUsdRouting,
        cbUsdBic,
        cbUsdCountry,
    };
}
